/**
 * In-app help content map.
 *
 * Keep entries short and action-oriented — the full USER_GUIDE.md
 * lives in `/app/memory/USER_GUIDE.md` for printable / shareable reference.
 *
 * Each entry has:
 *   title    — H3 in the drawer
 *   summary  — one-paragraph elevator pitch
 *   how      — array of imperative steps ("Click X", "Pick Y", …)
 *   tips     — array of pro-tips / gotchas
 *   related  — optional array of related topic keys (for cross-links)
 */
export const HELP = {
    // ------------------------------------------------------------------ pages
    dashboard: {
        title: "Dashboard",
        summary:
            "Your day at a glance — upcoming hearings, open matters, due tasks, recent drafts and (admin) the AI budget banner.",
        how: [
            "Click any card to jump straight into that module.",
            "Use Cmd+K to search across everything from here.",
        ],
        tips: [
            "If the AI budget banner shows < ₹500 remaining, top up the Universal Key from your Emergent profile before running heavy drafting.",
        ],
    },

    clients: {
        title: "Clients (CRM)",
        summary:
            "Your firm's client roster. Required before you can create a matter or raise an invoice.",
        how: [
            "Click '+ New client' to add a client.",
            "Capture GSTIN + PAN — GSTIN is mandatory for B2B invoices.",
            "Click a client row to see all their matters, total billed/collected, and notes.",
            "Use 'Archive' rather than 'Delete' to preserve the audit trail.",
        ],
        tips: [
            "The Conflict scanner cross-checks new matters against this roster automatically — make sure opposing-party names are filled correctly.",
        ],
        related: ["matters", "billing", "compliance_conflict"],
    },

    matters: {
        title: "Matters",
        summary:
            "Every case = one matter. Everything else (drafts, hearings, tasks, evidence, invoices) hangs off it.",
        how: [
            "Click '+ New matter' and fill: client, court, case number, practice area, opposing party.",
            "Open a matter to access 10 tabs: Overview, Documents, Research, Drafts, Hearings, Tasks, Workflow, Strategy, Activity, Notes.",
            "Use the Strategy tab to run AI assessments, build arguments, plan witnesses and draft grounds of appeal.",
        ],
        tips: [
            "Always set the Stage field accurately. When you close a matter as Won/Lost/Settled, the Analytics → Outcomes page lights up.",
            "Filling 'Opposing party' is what powers the Compliance → Conflict scanner.",
        ],
        related: ["strategy", "drafting", "hearings", "tasks", "billing"],
    },

    strategy: {
        title: "Strategy panel",
        summary:
            "Six sub-tools for litigation strategy on a single matter: Case strength, Risks, Arguments, Evidence, Witnesses, Appeal.",
        how: [
            "Strength — Click 'Run AI assessment' for a SWOT-style report with a 0–100 likelihood-of-success score.",
            "Risks — Capture likelihood × impact = severity score (1–16). Mark each as open / mitigating / accepted / closed.",
            "Arguments — Click 'Suggest (for us)' to let AI propose 4-6 arguments. Adopt the ones you like.",
            "Evidence — Plan exhibits with marks (P-1, R-3) and proof-purpose.",
            "Witnesses — Add a witness, then click 'Prep pack' to get 12-18 anticipated chief/cross examination questions.",
            "Appeal — Click 'Draft grounds with AI' to get 5-8 numbered grounds + prayer.",
        ],
        tips: [
            "AI buttons return a job ID and show a live progress timeline. They won't time out — walk away, the result will be waiting.",
            "Run the assessment AFTER you've uploaded documents to the matter. The AI uses them for context.",
        ],
        related: ["matters", "documents", "research"],
    },

    calendar: {
        title: "Calendar & Hearings",
        summary:
            "Month/week/day view of all hearings + tasks. Detects cross-court conflicts.",
        how: [
            "Click any date to add a hearing.",
            "Drag a hearing to reschedule.",
            "Filter by court, practice area or matter.",
            "After every hearing, mark it 'Held' and add a one-line Order — this powers your Outcomes analytics.",
        ],
        tips: [
            "Red conflict warnings show when two hearings in different courts overlap on the same day.",
        ],
        related: ["matters", "cause_lists", "analytics"],
    },

    cause_lists: {
        title: "Cause Lists",
        summary:
            "Auto-fetches daily cause lists from Supreme Court & Delhi High Court and cross-checks against your matters.",
        how: [
            "Pick the court + date.",
            "Click 'Fetch'.",
            "Matters matching your case numbers get ⭐ starred — click to jump in.",
            "For Delhi District Courts, upload the cause-list PDF manually — the app extracts your matters.",
        ],
    },

    court_search: {
        title: "Court Search (Indian Kanoon)",
        summary:
            "Real-time case-law search via Indian Kanoon. Natural language or citation queries.",
        how: [
            "Type a query: natural language ('Section 138 NI Act limitation') or citation ('(2023) 9 SCC 645').",
            "Filter by court / year / judge / doc type.",
            "Click a result to read the full judgment in the side pane.",
            "'Save to matter' creates a Research memo. 'Save to Precedent Library' adds it to your reusable collection.",
        ],
        tips: [
            "Devanagari queries are supported (दहेज प्रथा अधिनियम).",
        ],
        related: ["research", "precedents"],
    },

    tasks: {
        title: "Tasks",
        summary:
            "Lightweight task tracker — personal + matter-linked.",
        how: [
            "Drag tasks between Open / In progress / Done / Cancelled.",
            "Filter by matter, priority, due date.",
            "Use 'Due this week' / 'Overdue' quick toggles.",
        ],
        tips: [
            "Don't add tasks one by one for routine matters — apply a Playbook from Playbooks → Apply and get 6-10 dated tasks in one click.",
        ],
        related: ["playbooks", "matters"],
    },

    billing: {
        title: "Billing & Invoices",
        summary:
            "Indian-compliant billing: GST + TDS + SAC. Generates auto-numbered GST invoices in INR.",
        how: [
            "Time tab — Start/stop a timer or log manual entries. Hourly rate pulls from the active Fee Agreement.",
            "Expenses tab — Log expenses against one of 10 Indian-practice categories. Attach receipts.",
            "Invoices tab — Click 'Generate invoice' → pick client + matter scope + GST mode + SAC → submit. Invoice auto-numbers INV-{FY}-{NNN}.",
            "Agreements tab — Create Hourly / Fixed / Retainer / Contingency agreements per client.",
            "Click any invoice row to record payments (cash / NEFT / cheque / UPI / Razorpay).",
        ],
        tips: [
            "Contingency agreements trigger a BCI Rule 12 finding in Compliance → Self-audit. Use carefully.",
            "Razorpay is stubbed right now — set RAZORPAY_KEY_ID on the deployment to enable real payment links.",
            "Always fill client GSTIN before generating an invoice — required for B2B compliance.",
        ],
        related: ["clients", "analytics_financial", "compliance"],
    },

    analytics: {
        title: "Analytics",
        summary:
            "4-tab dashboard: Practice KPIs, Outcomes, Financial, Productivity.",
        how: [
            "Practice — 8 KPI cards (win rate, avg days-to-disposal, hearings held, drafts shipped, overdue tasks).",
            "Outcomes — Break down won/lost/settled by court / practice area / judge / stage.",
            "Financial — Monthly stacked-bar chart of invoiced / collected / expenses, plus top-clients table.",
            "Productivity — Daily billable vs non-billable hours + top matters by time.",
        ],
        tips: [
            "Outcomes only populate when matters are marked Won/Lost/Settled. Update the Stage field when matters close — it pays off in 6 months.",
        ],
        related: ["matters", "billing"],
    },

    research: {
        title: "Research Assistant",
        summary:
            "Citation-first legal research. Every answer cites Indian statutes + Supreme Court / High Court judgments, with page references.",
        how: [
            "Type your question — Hindi or English, can include section numbers.",
            "Optionally link to a matter so the answer is auto-saved as a memo.",
            "Click Search.",
            "Every citation is clickable and links to Indian Kanoon. Save the Q+A as a memo to keep it.",
        ],
        tips: [
            "No hallucinated cases — every citation is verified against Indian Kanoon's database before display.",
        ],
        related: ["court_search", "precedents", "drafting"],
    },

    drafting: {
        title: "Drafting",
        summary:
            "AI-assisted document drafting — plaints, notices, affidavits, bail applications, petitions, replies, custom.",
        how: [
            "Click '+ New draft' → pick draft type → link to a matter (recommended) → link source documents → write instructions.",
            "Use the 🎤 mic to dictate, or the ⚖️ Cite precedent button to insert citations from your library.",
            "Strict mode ON = AI cannot invent facts (recommended for filings). OFF = AI fills gaps with assumptions.",
            "Senior Counsel review = an extra LLM pass that critiques the draft. Slower but better quality.",
            "Click Generate. Watch the progress timeline. The result lands on the draft detail page.",
        ],
        tips: [
            "Always toggle Strict mode ON for anything you'll actually file.",
            "Refine button on the draft detail page lets you iterate — every refinement is saved as v2, v3, vN. You can revert any time.",
            "Pleading consistency check (on the draft detail page) catches party-name drift, date mismatches, prayer-vs-cause-of-action errors.",
        ],
        related: ["clauses", "precedents", "documents", "matters"],
    },

    cite_precedent: {
        title: "Cite a precedent",
        summary:
            "Insert a saved precedent into the drafting instructions in one click.",
        how: [
            "Click the ⚖️ icon next to the Instructions field.",
            "Search by citation, holding, statute or tag.",
            "Click a precedent — a citation snippet is inserted into Instructions.",
        ],
        tips: [
            "The list is pre-filtered by the linked matter's practice area, with favourable precedents at the top.",
            "Need a new precedent? Add it from /precedents first, then come back.",
        ],
        related: ["precedents", "drafting"],
    },

    clauses: {
        title: "Clause Bank",
        summary:
            "Reusable boilerplate clauses — jurisdiction, indemnity, arbitration, MSMED, RERA, and more across 27 categories.",
        how: [
            "Click 'New clause' to save a clause: title, category, body, language (en / hi / bilingual), tags.",
            "Click the copy icon on any clause card to copy the body to clipboard (also increments usage count).",
            "Set chamber_id to share a clause with your chamber.",
            "Filter by scope (Mine / Chamber / All), category, language, or full-text search.",
        ],
        tips: [
            "Most-used clauses bubble to the top automatically. Just keep copying — the bank organises itself.",
        ],
        related: ["drafting"],
    },

    precedents: {
        title: "Precedent Library",
        summary:
            "Your curated case-law collection — separate from looser Research memos.",
        how: [
            "Click 'Add precedent' to save: citation, court, year, bench, judge, holding, ratio, facts, your notes, statutes referred, URL.",
            "Tag each as favourable / adverse / neutral so the drafting picker can rank them right.",
            "Filter by court (Supreme / High / Tribunal / RERA / NCLT / etc), favourable-only, practice area or full-text.",
        ],
        tips: [
            "Use 'Save to Precedent Library' from Court Search results to populate this fast.",
            "The drafting page's Cite Precedent button pulls from this library.",
        ],
        related: ["court_search", "drafting"],
    },

    playbooks: {
        title: "Playbooks",
        summary:
            "Reusable step-by-step recipes — apply to a matter and N dated tasks appear at the right offsets.",
        how: [
            "Browse 8 system playbooks (S.138 NI Act, regular bail, civil money suit, Art.226 writ, RERA, CPA 2019, HC appeal, client intake).",
            "Click 'Apply' on any playbook → pick the matter + start date → click Apply.",
            "N tasks (typically 6-10) appear on the matter, dated at the right offsets, tagged with the playbook name.",
            "Build your own under My playbooks → New playbook. Format: 'Step title — day +N — priority high'.",
        ],
        related: ["tasks", "matters"],
    },

    compliance: {
        title: "BCI Compliance",
        summary:
            "Stay on the right side of the Bar Council of India and statutory regulators.",
        how: [
            "BCI rules — Reference catalogue of 7 key rules (1, 12, 22, 33, 36, 47, 49).",
            "Self-audit — Paste your website/LinkedIn copy. Scanner catches Rule 36 advertising language, Rule 12 contingency-fee agreements, Rule 49 employment issues.",
            "Conflict scan — Type proposed client + opposing parties. Cross-checks your roster + matters for duplicate clients, hostile representations, and existing-client conflicts.",
            "Regulatory feed — Live news from Supreme Court, BCI, Delhi HC, eGazette. Add your own RSS sources.",
        ],
        tips: [
            "Run the Conflict scan BEFORE you accept a new brief. It takes 2 seconds and avoids career-ending mistakes.",
        ],
        related: ["clients", "matters"],
    },

    compliance_conflict: {
        title: "Conflict scan",
        summary:
            "Cross-checks a proposed engagement against your client roster + opposing parties of existing matters.",
        how: [
            "Type the proposed client's name + opposing parties (one per line).",
            "Click 'Run conflict scan'.",
            "3 finding types appear: duplicate client (low), opposing-party-is-your-client (high), you-act-against-this-party (critical).",
        ],
        tips: [
            "Name matching is fuzzy — 'ABC Pvt. Ltd.' and 'abc pvt ltd' will both be caught.",
        ],
    },

    documents: {
        title: "Documents",
        summary:
            "Firm document library with automatic OCR, classification, summarisation and RAG-indexed chat.",
        how: [
            "Drag-and-drop files (PDF/DOCX/images). Link to a matter if relevant.",
            "Auto-pipeline runs in background: text extraction → OCR fallback (hin+eng) → page-cited chunking → classification → 5-line summary.",
            "Big files show live progress: 'Processing · page 12/87'.",
            "Open a document → 5 tabs: Summary, Chat (page-cited Q+A), Chronology (timeline of dated events), Translate, Red Flags (for contracts).",
        ],
        tips: [
            "Red Flags tab (on PDF contracts) catches unlimited indemnities, foreign-jurisdiction issues, MSMED Act violations, missing GST/RERA clauses.",
        ],
        related: ["matters", "research", "vault"],
    },

    vault: {
        title: "Knowledge Vault",
        summary:
            "Firm-wide knowledge base — files / notes / opinions that aren't tied to a specific matter.",
        how: [
            "Upload files to the vault.",
            "Use Vault chat to ask questions across the whole collection ('do we have an opinion on X?').",
        ],
        related: ["documents"],
    },

    chambers: {
        title: "Chambers",
        summary:
            "Team workspaces. By default everything is private — explicitly share resources with your chamber to collaborate.",
        how: [
            "Create a chamber: name + members.",
            "Invite advocates by email (they must be Google-sign-in ready).",
            "When creating a clause, precedent, playbook or matter, set chamber_id to share with the chamber.",
        ],
        tips: [
            "Admins can see audit logs but NOT the contents of other users' private matters.",
        ],
    },

    settings: {
        title: "Settings",
        summary:
            "Per-user preferences.",
        how: [
            "Set default court, jurisdiction, GST mode, notification preferences, default LLM model.",
            "Toggle Strict mode default for drafting.",
        ],
    },

    admin: {
        title: "Admin tools",
        summary:
            "Admin-only console. Manage users, chambers, audit logs and the LLM cost report.",
        how: [
            "Users tab — Allow-list emails, change roles, deactivate users.",
            "Chambers tab — All chambers in the firm.",
            "LLM costs tab — Spend report with KPIs, daily chart, breakdown by feature / model / user.",
            "Audit log tab — Every sensitive action with actor + timestamp.",
        ],
        related: ["llm_costs"],
    },

    llm_costs: {
        title: "LLM cost report",
        summary:
            "Admin-only spend dashboard. Every run_llm call is logged with provider, model, feature, user, tokens, cost in USD + INR.",
        how: [
            "Pick a period: Today / Week / Month / Quarter / FY.",
            "Pick a chart window: 7 / 30 / 90 days.",
            "Read 4 KPIs: total spend (INR + USD), tokens processed, reliability %.",
            "3 breakdown tables: by feature, by model, by user.",
            "Recent-calls log at the bottom shows the last 30 calls with provider / model / status / duration / cost.",
        ],
        tips: [
            "Token counts are estimates (chars/4). Exact billing comes from the Emergent Universal Key dashboard.",
            "Timeouts that recovered via fallback DO count as 'success' overall — reliability % is success_calls / total_calls.",
            "By user breakdown is useful for chamber-level billing reconciliation.",
        ],
    },

    // ------------------------------------------------------------------ cross-cutting
    global_search: {
        title: "Global search (Cmd+K)",
        summary:
            "Search across matters, documents, research memos, drafts, clauses and precedents — all from one box.",
        how: [
            "Press Cmd+K (Mac) / Ctrl+K (Windows) / / from anywhere.",
            "Type 2+ characters. Results stream in grouped by kind, with matches highlighted in yellow.",
            "Click any result to jump straight there.",
            "Esc closes the modal.",
        ],
    },

    voice: {
        title: "Voice input",
        summary:
            "Click any 🎤 icon to dictate. Supports Hindi + English + mixed (हिंग्लिश).",
        how: [
            "Click the mic icon → it starts listening.",
            "Speak normally for up to 60 seconds.",
            "Click again to stop. Transcribed text is inserted into the field.",
        ],
        tips: [
            "Best for short bursts. For long passages, break into 30-second chunks.",
        ],
    },

    async_jobs: {
        title: "Async AI jobs",
        summary:
            "AI actions that take more than ~10 seconds run as background jobs with a live progress timeline.",
        how: [
            "Click any AI button (e.g. Run assessment, Suggest arguments, Scan red flags).",
            "Watch the timeline below the button: queued → processing → completed.",
            "You can navigate away — the result still completes and appears in the list when you return.",
        ],
        tips: [
            "If Claude times out, the app auto-falls back to GPT-4o → Gemini. A 'served by fallback' chip will show on the result.",
        ],
    },
};

export function getHelp(topic) {
    return HELP[topic] || null;
}

export function listHelpTopics() {
    return Object.keys(HELP);
}
